import { AxiosError } from 'axios';
import { readToken } from '@cirqll/storybook/theme';
import { i18n } from '~/modules/i18n';
import { swalNotification, swalUnknownError } from './swal';

type CoreError = {
    code: number;
    message: string;
    reference: string;
    errors: Record<string, string[]>;
};

export function clearCoreErrors() {
    for (const element of document.querySelectorAll<HTMLElement>('[data-name]')) {
        if (element instanceof HTMLInputElement) {
            element.setCustomValidity('');
            element.title = element.dataset.title ?? '';
            delete element.dataset.title;

            // The visible box border lives on .inner-input-wrapper, not the input itself
            const wrapper = element.closest('.inner-input-wrapper');
            if (wrapper instanceof HTMLElement) {
                wrapper.style.removeProperty('border');
            } else {
                element.style.removeProperty('border');
            }
        }
    }
}

export function handleCoreError(error: unknown) {
    if (error instanceof AxiosError) {
        if (!error.response) {
            const { t } = i18n.global;
            void swalNotification(t('basic.connection_error'), true, 5000);
            return;
        }

        const data: CoreError = error.response.data;
        switch (error.status) {
            case 422:
                handleCore422Error(data);
                break;

            default:
                void swalNotification(data.message, true, 5000);
                break;
        }
    } else {
        void swalUnknownError();
    }
}

/**
 * Read per call rather than once at module scope: the theme can change while the app is open, and
 * this runs on an API response, so the value is always the one the user is currently looking at.
 */
function errorBorder() {
    return `1px solid ${readToken('danger')}`;
}

export function handleCore422Error(error: CoreError) {
    const elements = document.querySelectorAll<HTMLElement>('input[data-name], .inner-input-wrapper[data-name]:has(.chance-orb-wrapper), .ql-container[data-name]');
    const mappedElements = Array.from(elements).reduce((acc, element) => {
        acc[element.dataset.name!] ??= [];
        acc[element.dataset.name!].push(element);
        return acc;
    }, {} as Record<string, HTMLElement[]>);

    // Not every 422 is a validation error: e.g. an invalid filter only has a message
    for (const [name, description] of Object.entries(error.errors ?? {})) {
        const elements = mappedElements[name] ?? mappedElements[name.replace(/\.\d+$/, '')];
        if (!elements?.length) {
            // oxlint-disable-next-line no-console
            console.warn(`Missing data-name for ${name} error`);
            continue;
        }

        for (const element of elements) {
            if (element) {
                if (element instanceof HTMLInputElement) {
                    element.setCustomValidity(description[0] ?? 'Error');
                }
                element.dataset.title = element.title;
                element.title = description[0] ?? '';

                // The visible box border lives on .inner-input-wrapper, not the input itself
                const wrapper = element.closest('.inner-input-wrapper');
                if (wrapper instanceof HTMLElement) {
                    wrapper.style.border = errorBorder();
                } else {
                    element.style.border = errorBorder();
                }
            }
        }
    }

    void swalNotification(error.message, true, 10000);
}
